import { useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, InputBase, Stack } from '@mui/material'
import { ArrowBack as ArrowBackIcon, Mic as MicIcon } from '@mui/icons-material'

import { AttachmentAddButtons } from '@/apps/latest/features/attachments/AttachmentAddButtons'
import { CameraCaptureModal } from '@/apps/latest/features/attachments/CameraCaptureModal'
import { fileToAttachment } from '@/apps/latest/features/attachments/fileToAttachment'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { fieldInputSx } from '@/apps/latest/shared/styles/inlineInput'
import { useToast } from '@/shared/contexts/ToastContext'
import { normalizeBirthDate } from '@/shared/utils/birthDate'
import { RADIUS } from '@/shared/styles/radii'
import { FONT_WEIGHT, scaledFontSize } from '@/shared/styles/typography'

import type { NutritionCustomer, NutritionCustomerInput } from './api'
import { SexSelect } from './SexSelect'
import {
  useCreateNutritionCustomer,
  useDraftNutritionCustomer,
  useSimilarNutritionCustomers,
} from './useNutritionCustomers'

// 登録の 4 欄。問診票から読むのもこの 4 つだけ (相談内容・病歴は読ませない)。
const DRAFT_FIELDS = [
  { key: 'name', label: '氏名', placeholder: '山田 花子' },
  { key: 'name_kana', label: 'ふりがな', placeholder: 'やまだ はなこ' },
  { key: 'sex', label: '性別', placeholder: '女' },
  { key: 'birth_date', label: '生年月日', placeholder: '19650304' },
] as const

type DraftKey = (typeof DRAFT_FIELDS)[number]['key']
type Draft = Record<DraftKey, string>

// 左ペインの絞りの字を、字の形で入れる欄に分ける (絞りは氏名・ふりがな・生年月日の 3 つを引くので、打った字が氏名とは
// 限らない。ADR-0086 決定 3): 数字 (区切りつきも) は生年月日、かなだけはふりがな、それ以外は氏名。
export const draftFromText = (text: string): Draft => {
  const t = text.trim()
  const empty = { name: '', name_kana: '', sex: '', birth_date: '' }
  if (!t) return empty
  if (/^[0-9０-９/\-.年月日\s]+$/.test(t)) return { ...empty, birth_date: t }
  if (/^[ぁ-ゖァ-ヺー\s\u3000]+$/.test(t)) return { ...empty, name_kana: t }
  return { ...empty, name: t }
}

interface Props {
  // 左ペインの絞りの字 (字の形で氏名・ふりがな・生年月日のどれかに入れる)
  initialText: string
  // 候補の一覧へ戻る (「← 戻る」と Escape)
  onCancel: () => void
  // 登録できたら呼ぶ。問診票を読んでいれば、その画像も渡す (記録の添付にする)
  onRegistered: (
    customer: NutritionCustomer,
    questionnaire: Blob | null
  ) => void
  // 似たお客様の行を押したら呼ぶ (呼び出し側がそのカルテを開く。登録はしない)
  onSelectExisting: (customerId: string) => void
  // 呼び出し側が記録を作っている間 (二重送信を防ぐ)
  busy?: boolean
}

// 新しいお客様の登録: 4 欄 + 「問診票を読み取る」+ 前の面へ戻る「← 戻る」。登録の釦の上に、ふりがな か 生年月日 が
// 同じお客様を左ペインと同じ形の行で並べる (押すとそのカルテ。登録は止めない — 同じ生年月日の別人がいる。ADR-0086 決定 3)。
// nasu の患者新規登録 (カメラ 1 個 + Toast) と
// 同じ形で、モーダルにしない (入力 1 軸 + 実行 1 発。design-book 03-patterns/modals.md §1)。
// 問診票を読む口は資料と同じ icon 2 つ (撮る = modal を 1 枚で閉じる / 画像・PDF を選ぶ = スキャンの PDF)。
// 問診票は back が保存しないので、読めた欄だけ前埋めし、画像は呼び出し側が記録の添付に回す。
export const NewCustomerForm = ({
  initialText,
  onCancel,
  onRegistered,
  onSelectExisting,
  busy,
}: Props) => {
  const { showToast } = useToast()
  const [draft, setDraft] = useState<Draft>(() => draftFromText(initialText))
  const [questionnaire, setQuestionnaire] = useState<Blob | null>(null)
  // 登録できたお客様。記録が作れずに押し直したとき、もう一度登録しない (同じ人が 2 人になる)
  const [registered, setRegistered] = useState<NutritionCustomer | null>(null)
  const [captureOpen, setCaptureOpen] = useState(false)
  const questionnaireInput = useRef<HTMLInputElement | null>(null)
  const createCustomer = useCreateNutritionCustomer()
  const draftCustomer = useDraftNutritionCustomer()

  // 開いたら枠に focus を移す。検索欄に focus が残っていると、 打っていない状態の Escape が
  // ここまで上がってこない (keydown は focus の側から昇る)。
  const frame = useRef<HTMLDivElement>(null)
  useEffect(() => frame.current?.focus(), [])

  const handleQuestionnaire = (image: Blob) => {
    setCaptureOpen(false)
    setQuestionnaire(image)
    draftCustomer.mutate(image, {
      onSuccess: (read) => {
        setDraft((current) => ({ ...current, ...read }))
        const missing = DRAFT_FIELDS.filter((f) => !read[f.key]).map(
          (f) => f.label
        )
        showToast(
          missing.length === 0
            ? '問診票から 4 欄を読みました'
            : `読めなかった欄: ${missing.join('・')}`,
          missing.length === 0 ? 'success' : 'warning'
        )
      },
      onError: () => showToast('問診票を読み取れませんでした', 'error'),
    })
  }

  // 選んだ画像・PDF を撮った 1 枚と同じ形にして読む
  const handleQuestionnaireFile = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0]
    e.target.value = '' // 同じファイルを続けて選べるよう毎回 reset
    if (!file) return
    try {
      handleQuestionnaire(await fileToAttachment(file))
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : '画像読込に失敗しました',
        'error'
      )
    }
  }

  const handleRegister = () => {
    if (registered) {
      onRegistered(registered, questionnaire)
      return
    }
    if (!draft.name.trim()) return
    const birthDate = draft.birth_date.trim()
      ? normalizeBirthDate(draft.birth_date)
      : null
    if (draft.birth_date.trim() && birthDate === null) {
      showToast('生年月日は 19650304 の形で入れてください', 'error')
      return
    }
    const input: NutritionCustomerInput = {
      name: draft.name.trim(),
      name_kana: draft.name_kana.trim(),
      sex: draft.sex.trim(),
      birth_date: birthDate,
    }
    createCustomer.mutate(input, {
      onSuccess: (customer) => {
        setRegistered(customer)
        onRegistered(customer, questionnaire)
      },
      onError: () => showToast('お客様を登録できませんでした', 'error'),
    })
  }

  const isBusy = busy || createCustomer.isPending
  // 登録できた後は欄を固定する (押し直しは記録を作るだけで、直した値は送らない)
  const locked = registered !== null
  const similar = useSimilarNutritionCustomers(
    draft.name_kana.trim(),
    normalizeBirthDate(draft.birth_date)
  )
  const similarRows = locked ? [] : similar.data ?? []

  return (
    <Box
      ref={frame}
      tabIndex={-1}
      sx={{ px: 1.25, py: 1, outline: 'none' }}
      onKeyDown={(e) => {
        // 打っている途中でも Escape で候補へ戻れる (「← 戻る」と同じ)
        if (e.key === 'Escape') onCancel()
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onCancel}>
          戻る
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            component="span"
            sx={{
              fontSize: scaledFontSize('label'),
              fontWeight: FONT_WEIGHT.medium,
              color: colorTokens.fg.muted,
            }}
          >
            問診票を読み取る
          </Box>
          {draftCustomer.isPending ? (
            <CircularProgress size={14} sx={{ mx: 1 }} />
          ) : (
            <AttachmentAddButtons
              onCapture={() => setCaptureOpen(true)}
              onPick={() => questionnaireInput.current?.click()}
            />
          )}
        </Box>
      </Box>
      <input
        ref={questionnaireInput}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={(e) => void handleQuestionnaireFile(e)}
      />
      <Stack spacing={1}>
        {DRAFT_FIELDS.map((field) => (
          <Box
            key={field.key}
            sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
          >
            <Box
              component="label"
              htmlFor={`customer-${field.key}`}
              sx={{
                flexShrink: 0,
                width: 80,
                fontSize: scaledFontSize('label'),
                fontWeight: FONT_WEIGHT.medium,
                color: colorTokens.fg.muted,
              }}
            >
              {field.label}
            </Box>
            {field.key === 'sex' ? (
              <SexSelect
                value={draft.sex}
                onChange={(sex) => setDraft({ ...draft, sex })}
                disabled={locked}
              />
            ) : (
              <InputBase
                id={`customer-${field.key}`}
                value={draft[field.key]}
                onChange={(e) =>
                  setDraft({ ...draft, [field.key]: e.target.value })
                }
                placeholder={field.placeholder}
                inputProps={{ 'aria-label': field.label }}
                disabled={locked}
                sx={fieldInputSx}
              />
            )}
          </Box>
        ))}
      </Stack>
      {locked && (
        <Box
          sx={{
            mt: 1,
            fontSize: scaledFontSize('label'),
            color: colorTokens.fg.muted,
          }}
        >
          お客様は登録できました。直すのは左の一覧の行の ⋯
          の「お客様の情報」で。
        </Box>
      )}
      {similarRows.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              mb: 0.5,
              fontSize: scaledFontSize('tag'),
              fontWeight: FONT_WEIGHT.medium,
              color: colorTokens.fg.muted,
            }}
          >
            同じ ふりがな か 生年月日 のお客様
          </Box>
          {similarRows.map((customer) => (
            <Box
              key={customer.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectExisting(customer.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSelectExisting(customer.id)
              }}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 1.5,
                px: 1,
                py: 0.5,
                borderRadius: `${RADIUS.md}px`,
                cursor: 'pointer',
                '&:hover': { backgroundColor: colorTokens.bg.emphasis },
                '&:focus': { outline: 'none' },
                '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: scaledFontSize('label'),
                  color: colorTokens.fg.default,
                }}
              >
                {customer.name}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: scaledFontSize('tag'),
                  color: colorTokens.fg.faint,
                }}
              >
                {[customer.name_kana, customer.birth_date ?? '', customer.sex]
                  .filter(Boolean)
                  .join('  ')}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          size="medium"
          startIcon={<MicIcon />}
          disabled={isBusy || !draft.name.trim()}
          onClick={handleRegister}
        >
          {locked ? '記録を始める' : '登録して始める'}
        </Button>
      </Box>
      <CameraCaptureModal
        open={captureOpen}
        onClose={() => setCaptureOpen(false)}
        onCapture={handleQuestionnaire}
      />
    </Box>
  )
}
